import React, { useState, useEffect } from "react";
import { testimonial } from "../data/Data";
import CommonHeading from "../common/CommonHeading";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

// Custom Sleek & Prominent Next Arrow
function SampleNextArrow(props) {
  const { onClick } = props;
  return (
    <button
      type="button"
      className="hk-carousel-arrow hk-carousel-arrow--next"
      onClick={onClick}
      aria-label="Next slide"
    >
      <i className="fas fa-chevron-right"></i>
    </button>
  );
}

// Custom Sleek & Prominent Prev Arrow
function SamplePrevArrow(props) {
  const { onClick } = props;
  return (
    <button
      type="button"
      className="hk-carousel-arrow hk-carousel-arrow--prev"
      onClick={onClick}
      aria-label="Previous slide"
    >
      <i className="fas fa-chevron-left"></i>
    </button>
  );
}

// Helper function to render smart dynamic stars (5.0, 4.5, 4.0)
const renderStars = (rating = 5.0) => {
  const stars = [];
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 !== 0;

  for (let i = 0; i < fullStars; i++) {
    stars.push(<i key={`full-${i}`} className="fas fa-star hk-gold-star"></i>);
  }
  if (hasHalfStar) {
    stars.push(<i key="half" className="fas fa-star-half-alt hk-gold-star"></i>);
  }
  const emptyStars = 5 - Math.ceil(rating);
  for (let i = 0; i < emptyStars; i++) {
    stars.push(<i key={`empty-${i}`} className="far fa-star hk-empty-star"></i>);
  }
  return stars;
};

export default function Sliders({ isTop = false }) {
  const [selectedTestimonial, setSelectedTestimonial] = useState(null);
  const [popupOpen, setPopupOpen] = useState(false);
  
  // Interactive Helpful Votes state map (initialized with realistic counts)
  const [helpfulVotes, setHelpfulVotes] = useState(() => {
    const initial = {};
    testimonial.forEach((item, idx) => {
      initial[idx] = Math.floor(12 + (idx * 3) % 19); // realistic initial helpful count (12 to 30)
    });
    return initial;
  });

  const [votedItems, setVotedItems] = useState({});

  const handleHelpfulClick = (e, index) => {
    e.stopPropagation();
    setHelpfulVotes((prev) => ({
      ...prev,
      [index]: votedItems[index] ? prev[index] - 1 : prev[index] + 1,
    }));
    setVotedItems((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  // Lock body scroll when popup is open
  useEffect(() => {
    if (popupOpen) {
      document.body.classList.add("popup-open");
    } else {
      document.body.classList.remove("popup-open");
    }
    return () => {
      document.body.classList.remove("popup-open");
    };
  }, [popupOpen]);

  // Responsive Slider settings
  const settings = {
    dots: true,
    infinite: true,
    autoplay: true,
    autoplaySpeed: 3500,
    speed: 700,
    slidesToShow: 3,
    slidesToScroll: 1,
    centerMode: false,
    pauseOnHover: true,
    nextArrow: <SampleNextArrow />,
    prevArrow: <SamplePrevArrow />,
    responsive: [
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 992,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          centerMode: true,
          centerPadding: "20px",
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          centerMode: true,
          centerPadding: "10px",
        },
      },
    ],
  };

  const handleReadMoreClick = (item) => {
    setSelectedTestimonial(item);
    setPopupOpen(true);
  };

  const closePopup = () => {
    setPopupOpen(false);
    setSelectedTestimonial(null);
  };

  return (
    <section className={`hk-testimonial-section ${isTop ? "hk-top-section" : ""} my-0 position-relative`} style={{ padding: "10px 0 32px 0", background: "linear-gradient(180deg, #FAF6F0 0%, #F2EAE0 50%, #FAF6F0 100%)" }} aria-label="Guest Reviews">
      <div className="container position-relative" style={{ maxWidth: "1140px" }}>
        
        <CommonHeading
          heading="Testimonials"
          subtitle="Hear From Our"
          title="Happy Guests"
        />

        {/* Verified Rating Header Banner */}
        <div className="hk-rating-banner text-center mb-0 p-0 rounded-4 position-relative overflow-hidden">
          <div className="d-flex align-items-center justify-content-center gap-3 flex-wrap mb-2">
            <div className="d-flex align-items-center gap-1" style={{ color: "#FEA116", fontSize: "1.4rem" }}>
              <i className="fas fa-star"></i>
              <i className="fas fa-star"></i>
              <i className="fas fa-star"></i>
              <i className="fas fa-star"></i>
              <i className="fas fa-star"></i>
            </div>
            <strong className="hk-rating-score" style={{ fontSize: "1.35rem", color: "#0B1D37", fontFamily: "'Cinzel', serif" }}>
              4.9 / 5.0 Rating on Google & TripAdvisor
            </strong>
            <span className="hk-verified-badge-top px-3 py-1">
              <i className="fas fa-check-circle me-1"></i> 100% Verified Guest Reviews
            </span>
          </div>
          <p className="mb-0 text-muted" style={{ fontSize: "0.92rem", letterSpacing: "0.02em", color: "#53647C" }}>
            Based on 16+ verified guest stays at HariKunj Resort Jodhpur. Authentic feedback from real travelers.
          </p>
        </div>

        {/* Carousel of Modern Review Cards */}
        <Slider {...settings} className="hk-testimonial-carousel position-relative">
          {testimonial.map((item, index) => {
            const isVoted = votedItems[index];
            const votes = helpfulVotes[index] || 15;

            return (
              <div key={index} className="hk-testimonial-slide-wrapper">
                <div 
                  className="hk-modern-review-card position-relative"
                  onClick={() => handleReadMoreClick(item)}
                >
                  {/* Floating Quote Icon Watermark */}
                  <i className="fas fa-quote-right hk-quote-watermark position-absolute"></i>

                  {/* Top Bar: Verified Badge & Stay Date */}
                  <div className="d-flex align-items-center justify-content-between mb-3 position-relative" style={{ zIndex: 2 }}>
                    <span className="hk-card-verified-tag">
                      <i className="fas fa-shield-alt me-1"></i> Verified Stay
                    </span>
                    <span className="hk-card-stay-date">
                      <i className="far fa-calendar-check me-1"></i> {item.date || "July 2026"}
                    </span>
                  </div>

                  {/* User Profile Avatar with Metallic Ring */}
                  <div className="hk-avatar-ring-container mb-3 position-relative">
                    <div className="hk-avatar-glow-ring">
                      <img src={item.img} alt={`${item.name} profile`} className="hk-reviewer-avatar" />
                    </div>
                  </div>

                  {/* Review Excerpt Text */}
                  <div className="hk-review-body flex-grow-1 position-relative" style={{ zIndex: 2 }}>
                    <p className="hk-review-text mb-2">
                      &ldquo;
                      {item.description.length > 115
                        ? `${item.description.slice(0, 115)}...`
                        : item.description}
                      &rdquo;
                    </p>
                    {item.description.length > 115 && (
                      <button 
                        className="hk-read-story-btn border-0 bg-transparent p-0 mb-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleReadMoreClick(item);
                        }}
                      >
                        Read Full Story <i className="fas fa-arrow-right ms-1"></i>
                      </button>
                    )}
                  </div>

                  {/* Reviewer Details Footer */}
                  <div className="hk-review-card-footer pt-2 mt-auto border-top border-secondary-subtle position-relative" style={{ zIndex: 2 }}>
                    <div className="d-flex align-items-center justify-content-between gap-2">
                      <div className="text-start">
                        <h5 className="hk-reviewer-name m-0">{item.name}</h5>
                        <span className="hk-reviewer-prof-badge">{item.profession}</span>
                      </div>
                      
                      {/* Interactive Star Rating + Helpful Button */}
                      <div className="text-end d-flex flex-column align-items-end gap-1">
                        <div className="hk-stars-row">
                          {renderStars(item.rating || 5.0)}
                        </div>
                        
                        <button
                          type="button"
                          className={`hk-helpful-chip ${isVoted ? "hk-helpful-chip--active" : ""}`}
                          onClick={(e) => handleHelpfulClick(e, index)}
                          title="Click to mark as helpful"
                        >
                          <i className={`fas fa-thumbs-up me-1 ${isVoted ? "animate-bounce" : ""}`}></i>
                          <span>{votes}</span>
                        </button>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            );
          })}
        </Slider>
      </div>

      {/* Interactive Light Theme Modal Drawer for Full Review (No Scroll) */}
      {popupOpen && selectedTestimonial && (
        <div
          className="hk-review-modal-backdrop"
          role="dialog"
          aria-labelledby="hk-modal-title"
          aria-modal="true"
          onClick={closePopup}
        >
          <div
            className="hk-review-modal-card"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="d-flex align-items-center justify-content-between pb-2 mb-2 border-bottom border-secondary-subtle">
              <span className="hk-verified-badge-top px-3 py-1">
                <i className="fas fa-check-circle me-1"></i> Verified HariKunj Resort Stay
              </span>
              <button
                type="button"
                className="hk-modal-close-btn"
                onClick={closePopup}
                aria-label="Close modal"
              >
                &times;
              </button>
            </div>

            {/* Modal Body - Fixed height without scrolling */}
            <div className="text-center px-1">
              <div className="hk-avatar-ring-container mb-2 d-inline-block">
                <div className="hk-avatar-glow-ring" style={{ width: "72px", height: "72px" }}>
                  <img src={selectedTestimonial.img} alt={`${selectedTestimonial.name} profile`} className="hk-reviewer-avatar" />
                </div>
              </div>

              <h4 id="hk-modal-title" className="hk-reviewer-name h5 m-0 mb-1" style={{ color: "#0B1D37" }}>
                {selectedTestimonial.name}
              </h4>
              <span className="hk-reviewer-prof-badge mb-2 d-inline-block" style={{ color: "#53647C" }}>
                {selectedTestimonial.profession}
              </span>

              <div className="d-flex align-items-center justify-content-center gap-1 mb-3" style={{ color: "#FEA116", fontSize: "1.1rem" }}>
                {renderStars(selectedTestimonial.rating || 5.0)}
                <span className="ms-2 font-weight-bold" style={{ color: "#0B1D37", fontSize: "0.88rem" }}>
                  ({selectedTestimonial.rating ? selectedTestimonial.rating.toFixed(1) : "5.0"})
                </span>
              </div>

              <p className="hk-modal-quote-text mb-3">
                &ldquo;{selectedTestimonial.description}&rdquo;
              </p>

              <div className="p-2 p-sm-3 rounded-3 mb-3" style={{ background: "#FAF6F0", border: "1px solid rgba(212, 175, 55, 0.35)" }}>
                <div className="row g-2 text-start" style={{ fontSize: "0.82rem", color: "#1A283B" }}>
                  <div className="col-6">
                    <i className="fas fa-hotel text-warning me-1"></i> <strong>Property:</strong> HariKunj Resort
                  </div>
                  <div className="col-6">
                    <i className="fas fa-map-marker-alt text-warning me-1"></i> <strong>Location:</strong> Chopasni, Jodhpur
                  </div>
                  <div className="col-6">
                    <i className="fas fa-bed text-warning me-1"></i> <strong>Stay Type:</strong> Heritage Retreat
                  </div>
                  <div className="col-6">
                    <i className="fas fa-star text-warning me-1"></i> <strong>Rating:</strong> {selectedTestimonial.rating ? `${selectedTestimonial.rating.toFixed(1)} / 5.0` : "5.0 / 5.0"} Star
                  </div>
                </div>
              </div>

              <button
                type="button"
                className="hk-modal-done-btn py-2 px-4"
                onClick={closePopup}
              >
                Close Review
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Styled Scoped CSS */}
      <style>{`
        .hk-testimonial-section {
          background: linear-gradient(180deg, #FAF6F0 0%, #F2EAE0 50%, #FAF6F0 100%);
          color: #1A283B;
        }

        .hk-rating-banner {
          background: transparent;
          border: none;
          box-shadow: none;
        }

        .hk-verified-badge-top {
          background: rgba(34, 197, 94, 0.12);
          border: 1px solid rgba(34, 197, 94, 0.35);
          color: #15803d;
          font-size: 0.82rem;
          font-weight: 600;
          border-radius: 50px;
          letter-spacing: 0.03em;
        }

        /* CUSTOM BIG & SLICK CAROUSEL ARROWS */
        .hk-carousel-arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          z-index: 10;
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: #FFFFFF;
          border: 1.5px solid rgba(212, 175, 55, 0.5);
          color: #0B1D37;
          font-size: 1.1rem;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 8px 24px rgba(11, 29, 55, 0.15);
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .hk-carousel-arrow:hover {
          background: linear-gradient(135deg, #FEA116 0%, #D4AF37 100%);
          color: #050E1A;
          border-color: transparent;
          transform: translateY(-50%) scale(1.12);
          box-shadow: 0 10px 28px rgba(254, 161, 22, 0.45);
        }

        .hk-carousel-arrow--prev {
          left: -24px;
        }

        .hk-carousel-arrow--next {
          right: -24px;
        }

        @media (max-width: 1200px) {
          .hk-carousel-arrow--prev { left: -10px; }
          .hk-carousel-arrow--next { right: -10px; }
        }

        @media (max-width: 768px) {
          .hk-carousel-arrow {
            width: 40px;
            height: 40px;
            font-size: 0.95rem;
          }
          .hk-carousel-arrow--prev { left: 4px; }
          .hk-carousel-arrow--next { right: 4px; }
        }

        .hk-testimonial-carousel {
          padding: 0;
        }

        .hk-testimonial-slide-wrapper {
          padding: 12px 10px;
          height: 100%;
        }

        /* MODERN CLEAN REVIEW CARD */
        .hk-modern-review-card {
          background: #FFFFFF;
          border: none !important;
          border-radius: 24px;
          padding: 22px 20px;
          height: 350px;
          display: flex;
          flex-direction: column;
          box-shadow: 0 8px 24px rgba(11, 29, 55, 0.05);
          transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
          cursor: pointer;
          overflow: hidden;
        }

        .hk-modern-review-card:hover {
          transform: translateY(-8px) scale(1.02);
          box-shadow: 0 16px 36px rgba(254, 161, 22, 0.18);
        }

        /* Translucent Watermark Quote Icon */
        .hk-quote-watermark {
          top: 18px;
          right: 20px;
          font-size: 3.5rem;
          color: rgba(30, 62, 114, 0.06);
          pointer-events: none;
          z-index: 1;
          transition: color 0.3s ease;
        }

        .hk-modern-review-card:hover .hk-quote-watermark {
          color: rgba(254, 161, 22, 0.15);
        }

        .hk-card-verified-tag {
          font-size: 0.76rem;
          color: #15803d;
          font-weight: 600;
          letter-spacing: 0.03em;
          text-transform: uppercase;
        }

        .hk-card-stay-date {
          font-size: 0.75rem;
          color: #53647C;
        }

        /* Avatar Ring Styling */
        .hk-avatar-ring-container {
          display: flex;
          justify-content: center;
        }

        .hk-avatar-glow-ring {
          width: 76px;
          height: 76px;
          border-radius: 50%;
          padding: 3px;
          background: linear-gradient(135deg, #FEA116 0%, #D4AF37 100%);
          box-shadow: 0 6px 18px rgba(254, 161, 22, 0.3);
          transition: transform 0.3s ease;
        }

        .hk-modern-review-card:hover .hk-avatar-glow-ring {
          transform: scale(1.08);
        }

        .hk-reviewer-avatar {
          width: 100%;
          height: 100%;
          border-radius: 50%;
          object-fit: cover;
          border: 2px solid #ffffff;
        }

        /* Text and Typography */
        .hk-review-text {
          font-size: 0.92rem;
          line-height: 1.6;
          color: #1A283B;
          font-family: 'Poppins', sans-serif;
          font-style: italic;
        }

        .hk-read-story-btn {
          color: #FEA116;
          font-size: 0.82rem;
          font-weight: 600;
          letter-spacing: 0.03em;
          cursor: pointer;
          transition: color 0.2s ease;
        }

        .hk-read-story-btn:hover {
          color: #ff8c00;
          text-decoration: underline;
        }

        .hk-reviewer-name {
          color: #0B1D37;
          font-family: 'Cinzel', serif;
          font-size: 0.98rem;
          font-weight: 700;
          letter-spacing: 0.03em;
          text-transform: uppercase;
        }

        .hk-reviewer-prof-badge {
          display: inline-block;
          font-size: 0.76rem;
          color: #53647C;
          font-family: 'Poppins', sans-serif;
        }

        .hk-stars-row {
          color: #FEA116;
          font-size: 0.85rem;
          display: flex;
          gap: 2px;
        }

        .hk-gold-star {
          text-shadow: 0 0 4px rgba(254, 161, 22, 0.3);
        }

        .hk-empty-star {
          color: #CBD5E1;
        }

        /* Interactive Helpful Chip */
        .hk-helpful-chip {
          border: 1px solid rgba(30, 62, 114, 0.18);
          background: #FAF6F0;
          color: #1A283B;
          padding: 2px 10px;
          border-radius: 50px;
          font-size: 0.74rem;
          cursor: pointer;
          transition: all 0.3s ease;
          display: inline-flex;
          align-items: center;
        }

        .hk-helpful-chip:hover {
          background: rgba(254, 161, 22, 0.15);
          border-color: #FEA116;
          color: #FEA116;
        }

        .hk-helpful-chip--active {
          background: linear-gradient(135deg, #FEA116 0%, #D4AF37 100%) !important;
          color: #050E1A !important;
          border-color: transparent !important;
          font-weight: 700;
        }

        /* LIGHT THEME MODAL DRAWER OVERLAY - NO SCROLLING */
        .hk-review-modal-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(11, 29, 55, 0.65);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 16px;
          animation: hkFadeIn 0.3s ease-out;
        }

        .hk-review-modal-card {
          background: #FFFFFF;
          border: 1.5px solid rgba(212, 175, 55, 0.45);
          box-shadow: 0 20px 60px rgba(11, 29, 55, 0.2);
          border-radius: 24px;
          padding: 24px 22px;
          width: 100%;
          max-width: 480px;
          overflow: hidden;
          color: #1A283B;
          animation: hkPopIn 0.35s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }

        .hk-modal-close-btn {
          background: #FAF6F0;
          border: 1px solid rgba(30, 62, 114, 0.15);
          color: #0B1D37;
          font-size: 1.3rem;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.25s ease;
          line-height: 1;
        }

        .hk-modal-close-btn:hover {
          background: #FEA116;
          color: #050E1A;
          border-color: transparent;
          transform: rotate(90deg);
        }

        .hk-modal-quote-text {
          font-size: 0.95rem;
          line-height: 1.65;
          font-style: italic;
          color: #1A283B;
          font-family: 'Poppins', sans-serif;
          max-height: 130px;
          overflow: hidden;
          display: -webkit-box;
          -webkit-line-clamp: 5;
          -webkit-box-orient: vertical;
        }

        .hk-modal-done-btn {
          background: linear-gradient(135deg, #FEA116, #D4AF37);
          color: #050E1A;
          font-weight: 700;
          font-family: 'Cinzel', serif;
          border: none;
          border-radius: 50px;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          box-shadow: 0 6px 20px rgba(254, 161, 22, 0.35);
          cursor: pointer;
          transition: all 0.3s ease;
          font-size: 0.85rem;
        }

        .hk-modal-done-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 28px rgba(254, 161, 22, 0.5);
        }

        @keyframes hkFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes hkPopIn {
          from { opacity: 0; transform: scale(0.85); }
          to { opacity: 1; transform: scale(1); }
        }

        .hk-testimonial-carousel .slick-dots li button:before {
          font-size: 10px;
          color: #D4AF37;
          opacity: 0.4;
        }

        .hk-testimonial-carousel .slick-dots li.slick-active button:before {
          color: #FEA116;
          opacity: 1;
        }
      `}</style>
    </section>
  );
}
